import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)')?.matches;
    
    if (savedTheme === 'dark' || (!savedTheme && systemPrefersDark)) {
      setIsDarkMode(true);
      document.documentElement?.classList?.add('dark');
    } else {
      setIsDarkMode(false);
      document.documentElement?.classList?.remove('dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    
    if (newTheme) {
      document.documentElement?.classList?.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement?.classList?.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavClick = (path) => {
    window.location.href = path;
    setIsMobileMenuOpen(false);
  };

  const handleSmoothScroll = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="w-full px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <button 
              onClick={() => handleNavClick('/portfolio-homepage')}
              className="flex items-center space-x-2 group transition-all duration-200 ease-out-custom hover:opacity-80"
            >
              <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
                <Icon name="Code2" size={20} color="white" strokeWidth={2.5} />
              </div>
              <span className="text-xl font-bold text-foreground font-sans">
                DevPortfolio Pro
              </span>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => handleSmoothScroll('about')}
              className="text-muted-foreground hover:text-foreground transition-colors duration-200 ease-out-custom font-medium"
            >
              About
            </button>
            <button
              onClick={() => handleSmoothScroll('projects')}
              className="text-muted-foreground hover:text-foreground transition-colors duration-200 ease-out-custom font-medium"
            >
              Projects
            </button>
            <button
              onClick={() => handleSmoothScroll('skills')}
              className="text-muted-foreground hover:text-foreground transition-colors duration-200 ease-out-custom font-medium"
            >
              Skills
            </button>
            <button
              onClick={() => handleSmoothScroll('experience')}
              className="text-muted-foreground hover:text-foreground transition-colors duration-200 ease-out-custom font-medium"
            >
              Experience
            </button>
            <button
              onClick={() => handleSmoothScroll('contact')}
              className="text-muted-foreground hover:text-foreground transition-colors duration-200 ease-out-custom font-medium"
            >
              Contact
            </button>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="w-9 h-9"
            >
              <Icon 
                name={isDarkMode ? "Sun" : "Moon"} 
                size={18} 
                color="currentColor" 
              />
            </Button>
            
            <Button
              variant="outline"
              onClick={() => handleSmoothScroll('contact')}
              iconName="Mail"
              iconPosition="left"
              iconSize={16}
            >
              Get In Touch
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="w-9 h-9"
            >
              <Icon 
                name={isDarkMode ? "Sun" : "Moon"} 
                size={18} 
                color="currentColor" 
              />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
              className="w-9 h-9"
            >
              <Icon 
                name={isMobileMenuOpen ? "X" : "Menu"} 
                size={20} 
                color="currentColor" 
              />
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-lg bg-slate-900">
            <nav className="px-2 pt-2 pb-4 space-y-1">
              <button
                onClick={() => handleSmoothScroll('about')}
                className="block w-full text-left px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-all duration-200 ease-out-custom font-medium"
              >
                About
              </button>
              <button
                onClick={() => handleSmoothScroll('projects')}
                className="block w-full text-left px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-all duration-200 ease-out-custom font-medium"
              >
                Projects
              </button>
              <button
                onClick={() => handleSmoothScroll('skills')}
                className="block w-full text-left px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-all duration-200 ease-out-custom font-medium"
              >
                Skills
              </button>
              <button
                onClick={() => handleSmoothScroll('experience')}
                className="block w-full text-left px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-all duration-200 ease-out-custom font-medium"
              >
                Experience
              </button>
            
              <button
                onClick={() => handleSmoothScroll('contact')}
                className="block w-full text-left px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-all duration-200 ease-out-custom font-medium"
              >
                Contact
              </button>
              
              <div className="pt-2 mt-2 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => handleSmoothScroll('contact')}
                  iconName="Mail"
                  iconPosition="left"
                  iconSize={16}
                  fullWidth
                >
                  Get In Touch
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;